import classNames from 'classnames'
import { format } from 'date-fns'
import { Component } from 'react'
import { toast } from 'react-toastify'
import { Files, MyFile } from '../../components/files'
import { getString } from '../../locale'
import { getBase64 } from '../../utils/base64'
import { isDateValid } from '../../utils/date'
import { ChipId, IEspApp, parseEspBin } from './esp-bin-parser'

type Props = {
    binFile?: MyFile
    message?: string
    disabled?: boolean
    onChange: (binFile: MyFile, base64Bin: string, espApp: IEspApp) => any
    className?: string
    enforceChipId?: ChipId
}

type State = {
    espApp?: IEspApp
}

export class EspBinInput extends Component<Props, State> {
    state: State = this.getInitialState()

    getInitialState(): State {
        return {
            espApp: undefined,
        }
    }

    componentDidMount = async () => {
        if (this.props.binFile) {
            try {
                const base64Bin = await getBase64(this.props.binFile)
                const espApp = parseEspBin(base64Bin)
                this.setState({ espApp })
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        }
    }

    componentDidUpdate = async (prevProps: Props) => {
        if (
            (!prevProps.binFile && this.props.binFile) ||
            (this.props.binFile && prevProps.binFile && prevProps.binFile.id !== this.props.binFile.id)
        ) {
            try {
                const base64Bin = await getBase64(this.props.binFile)
                const espApp = parseEspBin(base64Bin)
                this.setState({ espApp })
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
                this.setState({ espApp: undefined })
            }
        }
    }

    async handleChange(files: MyFile[]) {
        if (files.length > 0) {
            try {
                const base64Bin = await getBase64(files[0])
                const espApp = parseEspBin(base64Bin)
                if (this.props.enforceChipId !== undefined && espApp.chipId !== this.props.enforceChipId) {
                    throw new Error(getString('esp_bin_input.unsupported_model_message', ChipId[this.props.enforceChipId]))
                }
                this.setState({ espApp })

                this.props.onChange(files[0], base64Bin, espApp)
            } catch (err) {
                console.error(err)
                toast.error((err as Error).message)
            }
        }
    }

    render() {
        return (
            <Files
                className={classNames(
                    'mb-5 h-60 shadow-md bg-white dark:bg-gray-800 rounded-xl border-dashed border-2 border-gray-400 dark:border-gray-600 dark:text-white text-lg flex flex-col justify-center items-center ease-linear duration-200',
                    this.props.className,
                )}
                dropActiveClassName="border-yellow-500 dark:border-yellow-500"
                onChange={this.handleChange.bind(this)}
                accepts={['.bin']}
                multiple={false}
                maxFileSize={15_000_000}
                minFileSize={1000}
                clickable
                disabled={this.props.disabled}
            >
                <div style={{ pointerEvents: 'none' }}>{getString('esp_bin_input.dnd_message')}</div>
                {this.renderBinFile()}
            </Files>
        )
    }

    private renderBinFile() {
        const { binFile } = this.props
        const { espApp } = this.state
        if (binFile === undefined && espApp === undefined) return undefined
        return (
            <div className="mt-5" style={{ pointerEvents: 'none' }}>
                {binFile && binFile.name && (
                    <div className="text-2xl text-center">
                        {binFile.name} ({binFile.sizeReadable})
                    </div>
                )}
                {espApp && (
                    <div>
                        {this.renderInfo(getString('static_traits.version'), espApp.version)}
                        {this.renderInfo(getString('static_traits.idf_ver'), espApp.idfVer)}
                        {this.renderInfo(getString('static_traits.model'), ChipId[espApp.chipId])}
                        {this.renderInfo(
                            '',
                            getString(
                                'esp_bin_input.compiled_at',
                                isDateValid(espApp.dateTime) ? format(espApp.dateTime, 'dd/MM/yyyy') : espApp.date,
                                isDateValid(espApp.dateTime) ? format(espApp.dateTime, 'HH:mm') : espApp.time,
                            ),
                        )}
                        {this.renderInfo(getString('esp_bin_input.app_elf_sha256'), espApp.appElfSha256Str)}
                    </div>
                )}
            </div>
        )
    }

    private renderInfo(label?: string, value?: string) {
        if (!value) return undefined
        return (
            <div className="mt-1 text-gray-500 text-sm flex justify-center">
                {label && <div className="me-1">{label}:</div>}
                <div>{value}</div>
            </div>
        )
    }
}
